import React, { useEffect, useState } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Hero from "@/components/sections/Hero";
import Introduction from "@/components/sections/Introduction";
import WhatArePeptides from "@/components/sections/WhatArePeptides";
import PeptidesVsPharmaceuticals from "@/components/sections/PeptidesVsPharmaceuticals";
import PeptideHistory from "@/components/sections/PeptideHistory";
import CurrentLandscape from "@/components/sections/CurrentLandscape";
import Applications from "@/components/sections/Applications";
import Conclusion from "@/components/sections/Conclusion";
import AboutTheAuthor from "@/components/sections/AboutTheAuthor";
import PremiumBanner from "@/components/sections/PremiumBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function FreePeptides() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasCompletedEmailCapture, setHasCompletedEmailCapture] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);

  useEffect(() => {
    // Check if the user has previously submitted their email
    const peptideEmail = localStorage.getItem("userRegistered");
    
    if (peptideEmail) {
      // User has already registered, show content immediately
      setHasCompletedEmailCapture(true);
      setContentLoaded(true);
    } else {
      // Show content immediately
      setContentLoaded(true);
      
      // Show the email capture modal after a short delay
      const timer = setTimeout(() => {
        setShowEmailModal(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Call the API to subscribe
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      if (response.ok) {
        // Save to localStorage
        localStorage.setItem("userRegistered", "true");
        localStorage.setItem("userEmail", email);
        
        // Update state
        setHasCompletedEmailCapture(true);
        setShowEmailModal(false);
        
        toast({
          title: "Thank you!",
          description: "You've been subscribed to our newsletter.",
        });
      } else {
        throw new Error("Failed to subscribe");
      }
    } catch (error) {
      console.error("Subscription error:", error);
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Modal can only be closed after submitting a valid email
  // We'll leave this function here for potential future functionality
  const closeModal = () => {
    // Do nothing - force user to input email
    toast({
      title: "Email Required",
      description: "Please enter your email to access the content",
      variant: "destructive"
    });
  };

  // Only show content if user has completed email capture
  return (
    <div className="min-h-screen flex flex-col bg-white font-sans text-gray-800">
      <Navbar />
      {contentLoaded && (
        <>
          <Hero />
          <main className="container mx-auto px-4 py-12 max-w-[900px]">
            <Introduction />
            <WhatArePeptides />
            <PeptidesVsPharmaceuticals />
            <PeptideHistory />
            <CurrentLandscape />
            <Applications />
            <PremiumBanner />
            <Conclusion />
            <AboutTheAuthor />
          </main>
        </>
      )}
      <Footer />
      
      {/* Custom email capture modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Get Free Access to Peptide Information</h2>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-6">
                Enter your email to unlock free peptide science content and receive updates on new resources.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
                  <Input 
                    id="email"
                    type="email" 
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <div className="flex flex-col space-y-2">
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
                    disabled={submitting}
                  >
                    {submitting ? "Processing..." : "Subscribe & Continue"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}